import React, { Component } from "react";
import {
  Button,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DoneIcon from "@material-ui/icons/Done";
import EditIcon from "@material-ui/icons/Edit";
import {
  buildLinkEquipment,
  equipmentIdOf,
  equipmentQuantity,
  withEquipmentQuantity
} from "../../../../util/EquipmentUtil";

class EquipmentEditor extends Component {
  state = {
    editing: false,
    selectedEquipmentId: "",
    selectedProcessorId: "",
    quantity: 1
  };

  equipment = () => Array.isArray(this.props.equipment) ? this.props.equipment : [];

  inventory = () => Array.isArray(this.props.inventory) ? this.props.inventory : [];

  inventoryItem = equipment => {
    const equipmentId = equipmentIdOf(equipment);
    return this.inventory().find(item => equipmentIdOf(item) === equipmentId);
  };

  processorsFor = equipment => {
    const current = this.inventoryItem(equipment) || equipment;
    return Array.isArray(current?.Processors) ? current.Processors : [];
  };

  updateEquipment = equipment => {
    this.props.onChange(equipment);
  };

  replaceAt = (index, item) => {
    const equipment = [...this.equipment()];
    equipment[index] = item;
    this.updateEquipment(equipment);
  };

  handleToggleEditing = () => {
    this.setState(state => ({ editing: !state.editing }));
  };

  handleExistingEquipmentChange = index => event => {
    const selected = this.inventory().find(item => equipmentIdOf(item) === event.target.value);
    if (!selected) {
      return;
    }

    const current = this.equipment()[index];
    const processors = Array.isArray(selected.Processors) ? selected.Processors : [];
    const currentProcessorId = current?.processor?.ProcessorID;
    const processor = processors.find(item => item.ProcessorID === currentProcessorId) || processors[0] || null;

    this.replaceAt(
      index,
      buildLinkEquipment(selected, processor, current?.quantity, current)
    );
  };

  handleExistingProcessorChange = index => event => {
    const current = this.equipment()[index];
    const processor = this.processorsFor(current)
      .find(item => item.ProcessorID === event.target.value);

    if (processor) {
      this.replaceAt(index, { ...current, processor });
    }
  };

  handleExistingQuantityChange = index => event => {
    const current = this.equipment()[index];
    this.replaceAt(index, withEquipmentQuantity(current, event.target.value));
  };

  handleDelete = index => () => {
    this.updateEquipment(this.equipment().filter((_, itemIndex) => itemIndex !== index));
  };

  handleNewEquipmentChange = event => {
    const selectedEquipmentId = event.target.value;
    const selected = this.inventory().find(item => equipmentIdOf(item) === selectedEquipmentId);
    const processor = Array.isArray(selected?.Processors) ? selected.Processors[0] : null;

    this.setState({
      selectedEquipmentId,
      selectedProcessorId: processor?.ProcessorID || ""
    });
  };

  handleNewProcessorChange = event => {
    this.setState({ selectedProcessorId: event.target.value });
  };

  handleNewQuantityChange = event => {
    this.setState({ quantity: equipmentQuantity(event.target.value) });
  };

  handleAdd = () => {
    const {
      selectedEquipmentId,
      selectedProcessorId,
      quantity
    } = this.state;
    const selected = this.inventory().find(item => equipmentIdOf(item) === selectedEquipmentId);
    const processor = Array.isArray(selected?.Processors)
      ? selected.Processors.find(item => item.ProcessorID === selectedProcessorId)
      : null;

    if (!selected || !processor) {
      return;
    }

    const equipment = [...this.equipment()];
    const existingIndex = equipment.findIndex(item =>
      equipmentIdOf(item) === selectedEquipmentId &&
      item?.processor?.ProcessorID === selectedProcessorId
    );

    if (existingIndex >= 0) {
      const existing = equipment[existingIndex];
      equipment[existingIndex] = withEquipmentQuantity(
        existing,
        equipmentQuantity(existing.quantity) + equipmentQuantity(quantity)
      );
    } else {
      equipment.push(buildLinkEquipment(selected, processor, quantity));
    }

    this.updateEquipment(equipment);
    this.setState({
      selectedEquipmentId: "",
      selectedProcessorId: "",
      quantity: 1
    });
  };

  renderEquipmentOptions = current => {
    const { selectedEquipmentId } = this.state;
    const currentId = current ? equipmentIdOf(current) : selectedEquipmentId;
    const isCurrentMissing = currentId && !this.inventory()
      .some(item => equipmentIdOf(item) === currentId);

    return [
      ...(isCurrentMissing ? [
        <MenuItem key={`missing-${currentId}`} value={currentId}>
          {current?.name || "Unavailable equipment"} (not in the current catalog)
        </MenuItem>
      ] : []),
      ...this.inventory().map(item => (
        <MenuItem key={equipmentIdOf(item)} value={equipmentIdOf(item)}>
          {item.name}
        </MenuItem>
      ))
    ];
  };

  renderProcessorOptions = equipment => {
    const processors = this.processorsFor(equipment);
    const selectedProcessor = equipment?.processor;
    const selectedProcessorId = selectedProcessor?.ProcessorID;
    const isCurrentMissing = selectedProcessorId && !processors
      .some(item => item.ProcessorID === selectedProcessorId);

    return [
      ...(isCurrentMissing ? [
        <MenuItem key={`missing-${selectedProcessorId}`} value={selectedProcessorId}>
          {selectedProcessor.Name || "Unavailable processor"} (not currently available)
        </MenuItem>
      ] : []),
      ...processors.map(processor => (
        <MenuItem key={processor.ProcessorID} value={processor.ProcessorID}>
          {processor.Name}
        </MenuItem>
      ))
    ];
  };

  renderSummary = () => {
    const {
      classes,
      defaultEquipment,
      defaultProcessor,
      useDefaultWhenEmpty
    } = this.props;
    const equipment = this.equipment();

    if (!equipment.length) {
      return (
        <div className={classes.equipmentEmptyState}>
          <Typography variant="body2" color="textSecondary">
            No equipment is mapped to this application.
          </Typography>
          {useDefaultWhenEmpty && defaultEquipment ? (
            <Typography variant="caption" color="textSecondary">
              If unchanged, {defaultEquipment.name}
              {defaultProcessor?.Name ? ` (${defaultProcessor.Name})` : ""} will be used as the default.
            </Typography>
          ) : (
            <Typography variant="caption" color="textSecondary">
              No equipment will be sent to Paysafe unless you add an item.
            </Typography>
          )}
        </div>
      );
    }

    return (
      <ul className={classes.summaryList}>
        {equipment.map((item, index) => (
          <li
            key={`${equipmentIdOf(item) || item.name}-${item?.processor?.ProcessorID || "processor"}-${index}`}
            className={classes.summaryItem}
          >
            <div>
              <Typography variant="body2">
                {item.name || "Equipment"} × {equipmentQuantity(item.quantity)}
              </Typography>
              {item.processor?.Name && (
                <Typography variant="caption" color="textSecondary">
                  {item.processor.Name}
                </Typography>
              )}
            </div>
            <Chip
              size="small"
              variant="outlined"
              label={item.type === "G" ? "Gateway" : "Terminal"}
            />
          </li>
        ))}
      </ul>
    );
  };

  renderExistingEditor = () => {
    const { classes } = this.props;
    const equipment = this.equipment();

    if (!equipment.length) {
      return (
        <Typography variant="body2" color="textSecondary" className={classes.equipmentEditorHint}>
          The application will contain no explicit equipment until you add an item below.
        </Typography>
      );
    }

    return equipment.map((item, index) => {
      const processors = this.processorsFor(item);
      return (
        <Paper
          key={`${equipmentIdOf(item) || item.name}-${item?.processor?.ProcessorID || "processor"}-${index}`}
          variant="outlined"
          className={classes.equipmentCard}
        >
          <div className={classes.equipmentCardFields}>
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              label="Equipment"
              value={equipmentIdOf(item)}
              onChange={this.handleExistingEquipmentChange(index)}
            >
              {this.renderEquipmentOptions(item)}
            </TextField>
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              label="Processor"
              value={item?.processor?.ProcessorID || ""}
              onChange={this.handleExistingProcessorChange(index)}
              error={!item?.processor?.ProcessorID}
              helperText={processors.length === 0 && !item?.processor?.ProcessorID
                ? "No processors are available for this equipment."
                : ""}
            >
              {this.renderProcessorOptions(item)}
            </TextField>
            <TextField
              type="number"
              variant="outlined"
              size="small"
              label="Quantity"
              value={equipmentQuantity(item.quantity)}
              onChange={this.handleExistingQuantityChange(index)}
              inputProps={{ min: 1, max: 999 }}
              className={classes.equipmentQuantity}
            />
            <Tooltip title="Remove equipment">
              <IconButton
                aria-label={`Remove ${item.name || "equipment"}`}
                onClick={this.handleDelete(index)}
                className={classes.equipmentDeleteButton}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </div>
        </Paper>
      );
    });
  };

  renderAddForm = () => {
    const { classes, inventoryLoading } = this.props;
    const {
      selectedEquipmentId,
      selectedProcessorId,
      quantity
    } = this.state;
    const selected = this.inventory().find(item => equipmentIdOf(item) === selectedEquipmentId);
    const processors = Array.isArray(selected?.Processors) ? selected.Processors : [];

    return (
      <Paper variant="outlined" className={classes.equipmentAddPanel}>
        <Typography variant="subtitle2" className={classes.equipmentAddTitle}>
          Add equipment
        </Typography>
        <div className={classes.equipmentCardFields}>
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            label="Equipment"
            value={selectedEquipmentId}
            onChange={this.handleNewEquipmentChange}
            disabled={inventoryLoading || this.inventory().length === 0}
            helperText={inventoryLoading ? "Loading equipment catalog…" : ""}
          >
            {this.renderEquipmentOptions()}
          </TextField>
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            label="Processor"
            value={selectedProcessorId}
            onChange={this.handleNewProcessorChange}
            disabled={!selectedEquipmentId || processors.length === 0}
            helperText={selectedEquipmentId && processors.length === 0 ? "No processors are available." : ""}
          >
            {processors.map(processor => (
              <MenuItem key={processor.ProcessorID} value={processor.ProcessorID}>
                {processor.Name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="number"
            variant="outlined"
            size="small"
            label="Quantity"
            value={quantity}
            onChange={this.handleNewQuantityChange}
            inputProps={{ min: 1, max: 999 }}
            className={classes.equipmentQuantity}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={this.handleAdd}
            disabled={!selectedEquipmentId || !selectedProcessorId}
            className={classes.equipmentAddButton}
          >
            Add
          </Button>
        </div>
      </Paper>
    );
  };

  render() {
    const { classes, error } = this.props;
    const { editing } = this.state;
    const unitCount = this.equipment()
      .reduce((total, item) => total + equipmentQuantity(item.quantity), 0);

    return (
      <section className={classes.summarySection}>
        <div className={classes.equipmentHeader}>
          <div>
            <Typography component="h2" variant="subtitle2" className={classes.summaryHeading}>
              Selected equipment
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {unitCount ? `${unitCount} unit${unitCount === 1 ? "" : "s"} selected` : "Review the mapping before submission"}
            </Typography>
          </div>
          <Button
            variant={editing ? "outlined" : "contained"}
            color="primary"
            size="small"
            startIcon={editing ? <DoneIcon /> : <EditIcon />}
            onClick={this.handleToggleEditing}
            className={classes.equipmentEditButton}
          >
            {editing ? "Done" : "Edit Equipment"}
          </Button>
        </div>

        {!editing && this.renderSummary()}

        <Collapse in={editing} unmountOnExit>
          <div className={classes.equipmentEditor}>
            {this.renderExistingEditor()}
            {this.renderAddForm()}
          </div>
        </Collapse>

        {error && (
          <Typography variant="caption" color="error" className={classes.equipmentError}>
            {error}
          </Typography>
        )}
      </section>
    );
  }
}

export default EquipmentEditor;
